/**
 * progressStore — localStorage map of { [videoId]: { position, at } } used to
 * resume a video where the viewer left off. `position` is millis; `at` is the
 * last-save timestamp, used to evict the least-recently-watched entries once
 * the map exceeds MAX_TRACKED_VIDEOS. Legacy entries are plain millis numbers
 * (no timestamp) — read as position with `at: 0`, so they evict first.
 */
const VIDEO_PROGRESS_KEY = "video_progress";
const MAX_TRACKED_VIDEOS = 100;
const RESTART_EDGE_SECONDS = 30;

const readAll = () => {
    try {
        const stored = localStorage.getItem(VIDEO_PROGRESS_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const positionOf = (entry) => (typeof entry === "number" ? entry : entry?.position);
const savedAtOf = (entry) => (typeof entry === "number" ? 0 : entry?.at || 0);

export const saveVideoProgress = (videoId, positionMillis) => {
    try {
        const progress = readAll();
        progress[videoId] = { position: positionMillis, at: Date.now() };

        const ids = Object.keys(progress);
        if (ids.length > MAX_TRACKED_VIDEOS) {
            ids
                .sort((a, b) => savedAtOf(progress[a]) - savedAtOf(progress[b]))
                .slice(0, ids.length - MAX_TRACKED_VIDEOS)
                .forEach((id) => delete progress[id]);
        }

        localStorage.setItem(VIDEO_PROGRESS_KEY, JSON.stringify(progress));
    } catch {
        // storage unavailable — resume is a nice-to-have, playback must not break
    }
};

/**
 * Resolves the position (seconds) a video should start at. Positions inside the
 * first or last RESTART_EDGE_SECONDS restart from zero — resuming there feels
 * broken.
 */
export const getStartPosition = (videoId, durationSeconds) => {
    const storedMillis = positionOf(readAll()[videoId]);
    if (!storedMillis || !durationSeconds) return 0;

    const storedSeconds = storedMillis / 1000;
    if (storedSeconds < RESTART_EDGE_SECONDS) return 0;
    if (storedSeconds > durationSeconds - RESTART_EDGE_SECONDS) return 0;

    return storedSeconds;
};
